import React from 'react';
import { TherapyModality } from '@/hooks/useTherapyModalities';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, Award, Building2, Sparkles, Target } from 'lucide-react';

interface TherapyCardProps {
  therapy: TherapyModality;
}

const TherapyCard: React.FC<TherapyCardProps> = ({ therapy }) => {
  // Display English content directly from database
  const name = therapy.name_en;
  const mechanism = therapy.mechanism_en;

  const getEvidenceBadgeColor = (level: string | null) => {
    switch (level) {
      case 'A': return 'bg-green-100 text-green-800 border-green-200';
      case 'B': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'C': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'D': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getEvidenceLabel = (level: string | null) => {
    switch (level) {
      case 'A': return 'Strong Evidence';
      case 'B': return 'Moderate Evidence';
      case 'C': return 'Limited Evidence';
      case 'D': return 'Emerging Evidence';
      default: return level;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-amber-100 p-4 space-y-3">
      {/* Header with name and badges */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-medium text-earth-900">{name}</h4>
        <div className="flex gap-1.5 flex-wrap">
          {therapy.evidence_level && (
            <Badge variant="outline" className={`text-xs ${getEvidenceBadgeColor(therapy.evidence_level)}`}>
              {getEvidenceLabel(therapy.evidence_level)}
            </Badge>
          )}
          {therapy.stream_type && (
            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
              {therapy.stream_type}
            </Badge>
          )}
        </div>
      </div>

      {/* Mechanism */}
      {mechanism && (
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">Mechanism</span>
            <p className="text-sm text-earth-700">{mechanism}</p>
          </div>
        </div>
      )}

      {/* Procedures */}
      {therapy.procedures && (
        <div className="flex items-start gap-2">
          <Target className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">Procedures</span>
            <p className="text-sm text-earth-700">{therapy.procedures}</p>
          </div>
        </div>
      )}

      {/* Duration and Sessions */}
      <div className="flex flex-wrap gap-4 text-sm">
        {therapy.session_length_minutes && (
          <div className="flex items-center gap-1.5 text-earth-600">
            <Clock className="h-4 w-4 text-green-500" />
            <span>{therapy.session_length_minutes} min</span>
          </div>
        )}
        {therapy.recommended_number_of_sessions && (
          <div className="flex items-center gap-1.5 text-earth-600">
            <Users className="h-4 w-4 text-indigo-500" />
            <span>{therapy.recommended_number_of_sessions} sessions</span>
          </div>
        )}
      </div>

      {/* Certifications */}
      {therapy.certifications && (
        <div className="flex items-start gap-2">
          <Award className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">Certifications</span>
            <p className="text-sm text-earth-700">{therapy.certifications}</p>
          </div>
        </div>
      )}

      {/* Organizations */}
      {therapy.organizations && (
        <div className="flex items-start gap-2">
          <Building2 className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-earth-600">Organizations</span>
            <p className="text-sm text-earth-700">{therapy.organizations}</p>
          </div>
        </div>
      )}

      {/* Target Domain */}
      {therapy.target_domain && (
        <div className="text-xs text-earth-500 pt-2 border-t border-amber-50">
          Target Domain: {therapy.target_domain}
        </div>
      )}
    </div>
  );
};

export default TherapyCard;
